<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue';
import { ArrowRight, Check } from 'lucide-vue-next';

const emit = defineEmits<{
  success: [];
}>();

const sliderRef = ref<HTMLDivElement | null>(null);
const trackRef = ref<HTMLDivElement | null>(null);
const isDragging = ref(false);
const offsetX = ref(0);
const verified = ref(false);

const trackWidth = computed(() => trackRef.value?.offsetWidth ?? 0);
const sliderWidth = computed(() => sliderRef.value?.offsetWidth ?? 0);
const maxOffset = computed(() => Math.max(0, trackWidth.value - sliderWidth.value - 2));
const progress = computed(() => (maxOffset.value > 0 ? offsetX.value / maxOffset.value : 0));

function startDrag(_e: MouseEvent | TouchEvent) {
  if (verified.value) return;
  isDragging.value = true;
  const clientX = 'touches' in _e ? _e.touches[0].clientX : _e.clientX;
  const startX = clientX - offsetX.value;

  function onMove(moveEvent: MouseEvent | TouchEvent) {
    if (!isDragging.value) return;
    const cx = 'touches' in moveEvent ? moveEvent.touches[0].clientX : moveEvent.clientX;
    let next = cx - startX;
    if (next < 0) next = 0;
    if (next > maxOffset.value) next = maxOffset.value;
    offsetX.value = next;
  }

  function onUp() {
    if (!isDragging.value) return;
    isDragging.value = false;
    if (progress.value >= 0.9) {
      offsetX.value = maxOffset.value;
      verified.value = true;
      emit('success');
    } else {
      offsetX.value = 0;
    }
    document.removeEventListener('mousemove', onMove as EventListener);
    document.removeEventListener('mouseup', onUp as EventListener);
    document.removeEventListener('touchmove', onMove as EventListener);
    document.removeEventListener('touchend', onUp as EventListener);
  }

  document.addEventListener('mousemove', onMove as EventListener);
  document.addEventListener('mouseup', onUp as EventListener);
  document.addEventListener('touchmove', onMove, { passive: false });
  document.addEventListener('touchend', onUp);
}

function reset() {
  verified.value = false;
  offsetX.value = 0;
}

defineExpose({ reset });

onUnmounted(() => {
  // 清理：onMove/onUp 已在 onUp 中自行移除，此处无需重复清理
  // 保留此注释以防后续开发者误加 removeEventListener
});
</script>

<template>
  <div
    ref="trackRef"
    class="relative h-10 select-none overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-card-bg)]"
  >
    <!-- 背景文字 -->
    <div
      class="absolute inset-0 flex items-center justify-center text-sm transition-opacity duration-300"
      :class="verified ? 'opacity-0' : 'opacity-100'"
    >
      <span class="text-[var(--color-caption)]">向右滑动完成验证</span>
    </div>

    <!-- 成功背景 -->
    <div
      class="absolute inset-0 flex items-center justify-center text-sm font-medium transition-opacity duration-300"
      :class="verified ? 'opacity-100' : 'opacity-0'"
      style="background: linear-gradient(90deg, color-mix(in srgb, var(--color-primary) 35%, var(--color-card-bg)) 0%, var(--color-primary-light) 100%); color: var(--color-primary);"
    >
      验证通过
    </div>

    <!-- 滑块 -->
    <div
      ref="sliderRef"
      class="absolute inset-y-0 z-10 flex h-full w-10 cursor-pointer items-center justify-center rounded-md bg-white shadow transition-colors duration-200"
      :class="{
        'cursor-default': verified,
        'border border-[var(--color-border)] text-[var(--color-caption)]': !verified,
        'border border-[var(--color-primary)] text-[var(--color-primary)]': verified,
      }"
      :style="{ transform: `translateX(${offsetX}px)` }"
      @mousedown="startDrag"
      @touchstart.prevent="startDrag"
    >
      <ArrowRight
        v-if="!verified"
        class="text-xl text-[var(--color-caption)]"
      />
      <Check
        v-else
        class="text-lg text-[var(--color-primary)]"
      />
    </div>
  </div>
</template>
